
import { useState } from 'react';

interface ChartLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const chartTypes = [
  {
    id: 1,
    name: 'Line Chart',
    icon: 'ri-line-chart-line',
    description: 'Perfect for showing trends over time',
    color: 'from-teal-500 to-cyan-500'
  },
  {
    id: 2,
    name: 'Bar Chart',
    icon: 'ri-bar-chart-box-line',
    description: 'Compare values across categories',
    color: 'from-blue-500 to-indigo-500'
  },
  {
    id: 3,
    name: 'Pie Chart',
    icon: 'ri-pie-chart-line',
    description: 'Show proportions and percentages',
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 4,
    name: 'Area Chart',
    icon: 'ri-line-chart-fill',
    description: 'Visualize cumulative totals',
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 5,
    name: 'Scatter Plot',
    icon: 'ri-bubble-chart-line',
    description: 'Identify correlations and patterns',
    color: 'from-orange-500 to-red-500'
  },
  {
    id: 6,
    name: 'Heatmap',
    icon: 'ri-grid-line',
    description: 'Display data density and intensity',
    color: 'from-yellow-500 to-orange-500'
  },
  {
    id: 7,
    name: 'Funnel Chart',
    icon: 'ri-filter-line',
    description: 'Track conversion processes',
    color: 'from-cyan-500 to-blue-500'
  },
  {
    id: 8,
    name: 'Gauge Chart',
    icon: 'ri-dashboard-line',
    description: 'Monitor KPIs and metrics',
    color: 'from-indigo-500 to-purple-500'
  },
  {
    id: 9,
    name: 'Radar Chart',
    icon: 'ri-radar-line',
    description: 'Compare multiple variables',
    color: 'from-pink-500 to-rose-500'
  },
  {
    id: 10,
    name: 'Treemap',
    icon: 'ri-layout-grid-line',
    description: 'Hierarchical data visualization',
    color: 'from-emerald-500 to-teal-500'
  },
  {
    id: 11,
    name: 'Waterfall Chart',
    icon: 'ri-bar-chart-grouped-line',
    description: 'Show cumulative effect of values',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 12,
    name: 'Candlestick',
    icon: 'ri-stock-line',
    description: 'Financial data analysis',
    color: 'from-red-500 to-orange-500'
  }
];

export default function ChartLibraryModal({ isOpen, onClose }: ChartLibraryModalProps) {
  const [selectedChart, setSelectedChart] = useState<number | null>(null);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-gray-800">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-6 flex justify-between items-center z-10">
          <div>
            <h2 className="text-3xl font-bold text-white">Chart Library</h2>
            <p className="text-gray-400 text-sm mt-1">Choose from 12+ professional chart types</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer w-10 h-10 flex items-center justify-center"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {chartTypes.map((chart) => (
              <div
                key={chart.id}
                onClick={() => setSelectedChart(chart.id)}
                className={`relative bg-gray-800 rounded-xl p-6 cursor-pointer transition-all hover:scale-105 border-2 ${
                  selectedChart === chart.id 
                    ? 'border-teal-500 shadow-lg shadow-teal-500/20' 
                    : 'border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${chart.color} flex items-center justify-center mb-4`}>
                  <i className={`${chart.icon} text-3xl text-white`}></i>
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-2">{chart.name}</h3>
                <p className="text-gray-400 text-sm">{chart.description}</p>

                {selectedChart === chart.id && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-2xl text-teal-400"></i>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-white mb-2">Customizable & Interactive</h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  All charts are fully customizable with colors, labels, tooltips, and animations. 
                  Export to PNG, SVG, or PDF. Real-time data updates supported. Responsive design 
                  works perfectly on all devices.
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
            <button
              disabled={!selectedChart}
              className="flex-1 px-6 py-3 bg-teal-500 text-gray-900 rounded-lg font-semibold hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectedChart ? 'Use This Chart' : 'Select a Chart'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
